import { useState, useEffect } from 'react';
import { FoodfluenceLocation } from '@foodfluence/shared';
import { collabApi } from '../services/api/collab';

interface UseCollabLocationResult {
  location: FoodfluenceLocation | null;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook for fetching a single location for a collab
 * @param locationId - The location ID to fetch
 * @param enabled - Whether to automatically fetch the location (default: true)
 */
export const useCollabLocation = (
  locationId: string | undefined,
  enabled: boolean = true
): UseCollabLocationResult => {
  const [location, setLocation] = useState<FoodfluenceLocation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!locationId || !enabled) {
      setLocation(null);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchLocation = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await collabApi.getLocationForCollab(locationId);
        if (response.success) {
          setLocation(response.data);
        } else {
          setError(response.error);
          setLocation(null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLocation(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLocation();
  }, [locationId, enabled]);

  return { location, loading, error };
}; 